import { DOCUMENT } from '@angular/common';
import { EventEmitter, inject, Injectable, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Client } from '@df/core/service/client';
import { TimeoutService } from '@df/ng/timeout.service';
import { BehaviorSubject } from 'rxjs';

export class ScrollEvent {
    constructor(
        public readonly scrollTop,
        public readonly isScrollingDown
    ) {}
}

@Injectable({
    providedIn: 'root'
})
/**
 * Service responsible for handling scroll events and providing scroll-related functionality.
 */
export class ScrollService implements OnDestroy {
    /**
     * Scroll events emitter
     */
    readonly scroll$ = new EventEmitter<ScrollEvent>();

    /**
     * Scroll top value
     */
    readonly scrollTop$ = new BehaviorSubject<number>(0);

    /**
     * If scrolling is blocked
     */
    readonly isBlocked$ = new BehaviorSubject<boolean | undefined>(undefined);

    /**
     * If last scrolling direction is down
     */
    readonly isScrollDown$ = new BehaviorSubject<boolean | undefined>(undefined);

    /**
     * Last scroll top
     */
    private lastScrollTop?: number;

    /**
     * Scroll top value at the point of locking
     */
    private lockedScrollTop?: number;

    private client = inject(Client);
    private timeoutService = inject(TimeoutService);
    private document = inject(DOCUMENT);

    /**
     * Cleanup function
     */
    private cleanup?: () => void;

    /**
     * Service initiator
     */
    constructor() {
        // only to fire scroll on 2 scroll events - if we resize window 1 event is fired, any scroll is more then that
        let doubleCheck = false;
        let timeout;

        this.client.rootElement$.pipe(takeUntilDestroyed()).subscribe((rootElement: Element | undefined) => {
            if (this.cleanup) {
                this.cleanup();
                this.cleanup = undefined;
            }

            if (rootElement) {
                const onScroll = () => {
                    const scrollTop = this.getScrollTop();
                    this.updateScrollTop(scrollTop);
                    if (doubleCheck) {
                        this.onScroll(scrollTop);
                    }
                    doubleCheck = true;
                    clearTimeout(timeout);
                    timeout = this.timeoutService.setTimeout(() => (doubleCheck = false), 100);
                };
                const scrollingElement = ['HTML', 'BODY'].includes(rootElement.tagName) ? this.document : rootElement;
                scrollingElement.addEventListener('scroll', onScroll, { passive: true });
                this.cleanup = () => {
                    scrollingElement!.removeEventListener('scroll', onScroll);
                };
                this.updateScrollTop();
            }
        });
    }

    /**
     * Post destroy cleanup
     */
    ngOnDestroy(): void {
        this.cleanup?.();
    }

    /**
     * Get current scrolltop
     */
    getScrollTop(): number {
        if (this.lockedScrollTop !== undefined) {
            return this.lockedScrollTop;
        }
        return this.client.getRootElement()?.scrollTop || 0;
    }

    /**
     * Set scroll top to root element
     */
    setScrollTop(scrollTop: number) {
        if (this.lockedScrollTop !== undefined) {
            this.lockedScrollTop = scrollTop;
            const scrollingElement = this.client.getRootElement();
            scrollingElement.style.top = `-${this.lockedScrollTop}px`;
        } else {
            this.client.getRootElement().scrollTop = scrollTop;
        }
    }

    /**
     * Block scrolling of the app root element
     */
    blockScroll(): () => void {
        if (typeof document == 'undefined') {
            return () => {};
        }
        const scrollingElement = this.client.getRootElement();
        if (this.lockedScrollTop === undefined) {
            this.lockedScrollTop = scrollingElement.scrollTop;
            this.isBlocked$.next(true);

            scrollingElement.style.position = 'fixed';
            scrollingElement.style.top = `-${this.lockedScrollTop}px`;
            scrollingElement.style.overflowY = 'scroll';
            scrollingElement.style.height = 'auto';
            scrollingElement.scrollTop = 0;
        }

        return () => {
            if (typeof this.lockedScrollTop !== 'undefined') {
                scrollingElement.style.position = '';
                scrollingElement.style.top = '';
                scrollingElement.style.overflowY = '';
                scrollingElement.style.height = '';
                scrollingElement.scrollTop = this.lockedScrollTop;
                this.lockedScrollTop = undefined;
                this.isBlocked$.next(false);
            }
        };
    }

    /**
     * Method called when scroll event is fired
     */
    private onScroll(scrollTop: number = this.getScrollTop()): void {
        // check if we are scrolling down
        const isScrollingDown = this.lastScrollTop === undefined ? true : scrollTop > this.lastScrollTop;

        // update last entry
        this.lastScrollTop = scrollTop;

        // emit scroll direction event
        if (this.isScrollDown$.value !== isScrollingDown) {
            this.isScrollDown$.next(isScrollingDown);
        }

        // emit event
        this.scroll$.emit(new ScrollEvent(scrollTop, isScrollingDown));
    }

    private updateScrollTop(scrollTop: number = this.getScrollTop()): void {
        if (scrollTop !== this.scrollTop$.getValue()) {
            this.scrollTop$.next(scrollTop);
        }
    }
}
